import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type UpdateLeadAttributeVariables = {
	companyId: string;
	/** Form field id (the attribute's `{fieldId}`). */
	fieldId: string;
	/** Selected option id — the `Value` the BE's `SetAttributeRequest` expects. */
	value: string;
};

/**
 * Writes a single HubSpot attribute value on the lead. BE-confirmed path:
 * `PUT lead/{companyId}/Lead/Field/{fieldId}` with body `{ value }`
 * (`SetAttributeRequest`) — persists to the HubSpot **Company** object, NO deal
 * required (#18844). Mirrors the legacy `LeadAttribute` save, which sends the
 * selected option id. On success the lead query is invalidated so the pill
 * reflects the new value.
 *
 * No `MutationCache` is configured (see `packages/main/CLAUDE.md`), so callers
 * must pass an explicit `onError` to surface failures (the BE / HubSpot can
 * reject post-hoc).
 */
export function useUpdateLeadAttribute() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<void, Error, UpdateLeadAttributeVariables>({
		mutationFn: async ({ companyId, fieldId, value }) => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<unknown>(
				ImpersonationBroker.composeApiUrl({ urlPath: `lead/${companyId}/Lead/Field/${fieldId}` }),
				'PUT',
				{ value }
			);
			if (!result.success) {
				throw asApiError(result);
			}
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
		},
	});
}
